/**
 * PATH: packages/ui/src/components/settings/OutlookAdminConsentBanner.tsx
 * PURPOSE: Banner shown when Outlook OAuth fails due to missing admin consent
 * WHY: Multi-tenant Microsoft 365 orgs require tenant admin to approve the app
 *      before individual users can authenticate. This guides the user through
 *      getting that approval.
 *
 * FLOW:
 *   ┌──────────────┐     ┌──────────────────────┐     ┌─────────────┐
 *   │ Error params  │  →  │ OutlookAdminConsent  │  →  │ Copy URL /  │
 *   │ from callback │     │      Banner          │     │ Retry login │
 *   └──────────────┘     └──────────────────────┘     └─────────────┘
 */

import { useState, useCallback } from 'react'
import { ShieldAlert, Copy, Check, RefreshCw, ExternalLink } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface OutlookAdminConsentBannerProps {
  adminConsentUrl: string
  onRetry?: () => void
  onDismiss?: () => void
  className?: string
}

export function OutlookAdminConsentBanner({
  adminConsentUrl,
  onRetry,
  onDismiss,
  className,
}: OutlookAdminConsentBannerProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    if (!adminConsentUrl) return
    try {
      await navigator.clipboard.writeText(adminConsentUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = adminConsentUrl
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    }
  }, [adminConsentUrl])

  return (
    <div
      className={cn(
        'rounded-xl border border-amber-300 dark:border-amber-700',
        'bg-amber-50 dark:bg-amber-950/30 p-5',
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/50 flex-shrink-0">
          <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-amber-900 dark:text-amber-100 text-sm">
            Admin Approval Required
          </h4>
          <p className="mt-1 text-sm text-amber-800 dark:text-amber-200">
            Your organisation's IT admin needs to approve{' '}
            <span className="font-medium">Quotech Email Processor</span> before
            you can connect Outlook.
          </p>

          {adminConsentUrl && (
            <div className="mt-3 space-y-2">
              <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
                Send this link to your IT admin:
              </p>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0 p-2.5 rounded-lg bg-white dark:bg-slate-800 border border-amber-200 dark:border-amber-800">
                  <p className="text-xs text-slate-600 dark:text-slate-300 truncate font-mono">
                    {adminConsentUrl}
                  </p>
                </div>
                <button
                  onClick={handleCopy}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors',
                    copied
                      ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                      : 'bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-100 hover:bg-amber-300 dark:hover:bg-amber-700',
                  )}
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      Copy
                    </>
                  )}
                </button>
              </div>

              <a
                href={adminConsentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300 hover:underline mt-1"
              >
                <ExternalLink className="w-3 h-3" />
                Or open the link yourself (if you are the admin)
              </a>
            </div>
          )}

          {!adminConsentUrl && (
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
              Ask your IT admin to grant consent for Quotech Email Processor in
              the Microsoft Entra admin center.
            </p>
          )}

          <div className="mt-4 flex items-center gap-3">
            {onRetry && (
              <button
                onClick={onRetry}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Retry Outlook Connection
              </button>
            )}
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="px-3 py-2 rounded-lg text-xs text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors"
              >
                Dismiss
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default OutlookAdminConsentBanner
